import React, { useCallback, useEffect, useState } from 'react';
import { invitesAPI, workspaceAPI } from '../../lib/api';
import { IssuedInviteLink } from '../../pages/Account';

// Guests of the open workspace: people outside the account, invited to work on
// its shows. Guests are free. What they can do depends on whether they pay for
// Mission Control somewhere themselves — worked out by the server each time, so
// it is shown here as it stands today.

const input = 'bg-[#0a0a0a] border border-[#2a2a2a] rounded px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500';

function formatDate(d) {
  return new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function GuestsSection({ workspaceId }) {
  const [guests, setGuests] = useState([]);
  const [invites, setInvites] = useState([]);
  const [email, setEmail] = useState('');
  const [endDate, setEndDate] = useState('');
  const [inviting, setInviting] = useState(false);
  const [issued, setIssued] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    workspaceAPI.guests().then(r => setGuests(r.data)).catch(() => setError('Failed to load guests'));
    invitesAPI.list({ workspace_id: workspaceId }).then(r => setInvites(r.data)).catch(() => {});
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  async function handleInvite(e) {
    e.preventDefault();
    if (!email.trim()) return;
    setInviting(true);
    setError('');
    try {
      const r = await invitesAPI.create({
        kind: 'guest',
        workspace_id: workspaceId,
        email: email.trim(),
        // Access lasts to the end of the chosen day.
        access_expires_at: endDate ? new Date(`${endDate}T23:59:59`).toISOString() : null,
      });
      setIssued({ email: r.data.email, link: r.data.link });
      setEmail('');
      setEndDate('');
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create the invite');
    } finally {
      setInviting(false);
    }
  }

  async function handleRemove(guest) {
    if (!confirm(`End ${guest.name || guest.email}'s access to this workspace?`)) return;
    try {
      await workspaceAPI.removeGuest(guest.id);
      setGuests(prev => prev.filter(g => g.id !== guest.id));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove the guest');
    }
  }

  async function handleWithdraw(invite) {
    try {
      await invitesAPI.withdraw(invite.id);
      setInvites(prev => prev.filter(i => i.id !== invite.id));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to withdraw the invite');
    }
  }

  return (
    <div>
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Guests ({guests.length})</div>
      <p className="text-xs text-gray-600 mb-3 leading-relaxed">
        Free access to this workspace for people outside the account. A guest can fill in scripts, notes and graphics
        text and run the show; one who pays for Mission Control themselves gets full production access too.
      </p>

      {guests.length > 0 && (
        <div className="space-y-1 mb-3">
          {guests.map(guest => (
            <div key={guest.id} className="flex items-center gap-3 px-3 py-2 bg-[#0a0a0a] border border-[#1a1a1a] rounded">
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">{guest.name || guest.email}</div>
                <div className="text-[11px] text-gray-600 truncate">
                  {guest.name ? `${guest.email} · ` : ''}
                  {guest.expired ? <span className="text-red-400">access ended {formatDate(guest.expires_at)}</span>
                    : guest.expires_at ? `until ${formatDate(guest.expires_at)}` : 'no end date'}
                </div>
              </div>
              <span
                className={`text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 ${
                  guest.guest_level === 'full' ? 'text-green-400 border border-green-900' : 'text-amber-400 border border-amber-900'
                }`}
                title={guest.guest_level === 'full' ? 'Pays for Mission Control — full production access' : 'Script, notes, graphics text and running the show'}
              >
                {guest.guest_level === 'full' ? 'Full' : 'Text & run'}
              </span>
              <button onClick={() => handleRemove(guest)} className="text-xs text-gray-500 hover:text-red-400 transition-colors">
                Remove
              </button>
            </div>
          ))}
        </div>
      )}

      {issued ? (
        <IssuedInviteLink issued={issued} onDone={() => setIssued(null)} />
      ) : (
        <form onSubmit={handleInvite} className="space-y-2">
          <div className="flex gap-2">
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="Invite a guest by email"
              className={`flex-1 min-w-0 ${input}`}
            />
            <button
              type="submit"
              disabled={inviting || !email.trim()}
              className="px-3 py-2 bg-blue-700 hover:bg-blue-600 disabled:opacity-40 text-white text-xs font-medium rounded transition-colors whitespace-nowrap"
            >
              {inviting ? 'Inviting…' : 'Invite'}
            </button>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-[11px] text-gray-600">Access ends</label>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className={`${input} py-1 text-xs`} />
            <span className="text-[11px] text-gray-600">{endDate ? '' : 'optional — leave blank for no end date'}</span>
          </div>
        </form>
      )}

      {invites.length > 0 && (
        <div className="mt-3 space-y-1">
          {invites.map(invite => (
            <div key={invite.id} className="flex items-center gap-3 px-3 py-1.5 text-xs">
              <span className="flex-1 min-w-0 truncate text-gray-400">{invite.email}</span>
              <span className={invite.expired ? 'text-red-400' : 'text-gray-600'}>
                {invite.expired ? 'invite expired' : 'invite pending'}
              </span>
              <button onClick={() => handleWithdraw(invite)} className="text-gray-500 hover:text-red-400">Withdraw</button>
            </div>
          ))}
        </div>
      )}

      {error && <p className="text-xs text-red-400 mt-2">{error}</p>}
    </div>
  );
}
